import { useEffect, useRef } from "react";
import { isJobActive } from "@/api/queries";
import { copyText } from "@/copy/copy";
import { useToast } from "@/components/Toast";
import type { FamilyDocument } from "@/generated/backend/v1/family";
import { PlanJob_State, type PlanJob, type StoredPlan } from "@/generated/backend/v1/plan";

/**
 * The household's latest plan job when it failed after the plan shown was made (GET /plan's `failedJob`;
 * a FAILED `pendingJob` is read the same way).
 */
export const failedPlanJob = (stored: StoredPlan | null | undefined): PlanJob | undefined =>
  stored?.failedJob ??
  (stored?.pendingJob?.state === PlanJob_State.FAILED ? stored.pendingJob : undefined);

/**
 * The failed job, when it was for the household's current version. A newer save hides it at once,
 * before GET /plan is refetched.
 */
export const rebuildFailedJob = (
  family: FamilyDocument | null | undefined,
  stored: StoredPlan | null | undefined,
): PlanJob | undefined => {
  const failed = failedPlanJob(stored);
  return failed && failed.familyVersion >= (family?.version ?? 0) ? failed : undefined;
};

/** Whether the job for the household's current version failed. */
export const rebuildFailed = (
  family: FamilyDocument | null | undefined,
  stored: StoredPlan | null | undefined,
) => !!rebuildFailedJob(family, stored);

/** A plan job is running, or the household is newer than the stored plan and its job hasn't failed. */
export function isRebuilding(
  family: FamilyDocument | null | undefined,
  stored: StoredPlan | null | undefined,
): boolean {
  if (!stored?.plans?.abiPlan) return false; // no plan yet: the page shows its own "building" state
  if (isJobActive(stored.pendingJob)) return true;
  if (rebuildFailed(family, stored)) return false;
  return !!family && family.version > stored.familyVersion;
}

/** Toasts when a rebuild ends, on any page; `quiet` where the page says it (Update Data). */
export function usePlanUpdatedToast(rebuilding: boolean, failed: boolean, quiet: boolean) {
  const toast = useToast();
  const was = useRef(rebuilding);
  useEffect(() => {
    if (was.current && !rebuilding && !quiet)
      toast(copyText(failed ? "plan.failedToast" : "plan.updated"));
    was.current = rebuilding;
  }, [rebuilding, failed, quiet, toast]);
}
